import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { clearDashboardHierarchyReseedFlag } from "../../redux/actions/homeAction";
import { validatePartnerRoleType } from "../../utils/common";
import { filterNames } from "../../utils/constants";

/**
 * After Home/back, RESET_FILTERS_TO_DEFAULT sets needsDashboardHierarchyReseed so mobility
 * business-type users get one hierarchy seed (App.fetchAPI), same as post-login.
 * The reseed flag must always clear when this effect finishes — including when the user
 * navigates away before await completes — otherwise the flag stays true and fetchAPI runs on
 * every later dashboard switch, resetting filters to defaults incorrectly.
 */
const DashboardHierarchyReseedListener = ({ runHierarchySeed }) => {
  const dispatch = useDispatch();
  const location = useLocation();

  const { partnerRoleType, filterValue, needsDashboardHierarchyReseed } =
    useSelector((state) => state.homeReducer);

  useEffect(() => {
    const path = location.pathname;
    // Stay on Home: nothing to seed until user opens a dashboard.
    if (path === "/" || path === "") return;
    // No pending reseed (e.g. user switched dashboards without going through reset).
    if (!needsDashboardHierarchyReseed) return;
    // Same branch as fetchAPI in routes: non-business-type roles use a different flow; clear flag only.
    if (!validatePartnerRoleType(partnerRoleType)?.businessType) {
      dispatch(clearDashboardHierarchyReseedFlag());
      return;
    }
    // Hierarchy reseed applies to mobility site only; other sites clear the flag.
    if (
      filterValue?.site?.toLowerCase() !== filterNames.mobility.toLowerCase()
    ) {
      dispatch(clearDashboardHierarchyReseedFlag());
      return;
    }
    (async () => {
      try {
        if (typeof runHierarchySeed === "function") {
          await runHierarchySeed();
        }
      } finally {
        dispatch(clearDashboardHierarchyReseedFlag());
      }
    })();
  }, [
    location.pathname,
    needsDashboardHierarchyReseed,
    partnerRoleType,
    filterValue?.site,
    runHierarchySeed,
    dispatch,
  ]);

  return null;
};

export default DashboardHierarchyReseedListener;
